import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Field } from "@cntrl/ui";
import { useAuth } from "../../app/auth/useAuth.js";
import { AuthCard } from "./AuthCard.js";
import styles from "./AuthForm.module.scss";

const schema = z.object({ email: z.string().min(1, "Enter your email").email("Enter a valid email") });
type ForgotValues = z.infer<typeof schema>;

export interface ForgotPasswordScreenProps {
  onBack: () => void;
}

/**
 * Request a reset LINK. Always shows the same confirmation regardless of whether the email exists
 * (no account enumeration — the server always 202s). A link, never a plaintext temp password.
 */
export function ForgotPasswordScreen({ onBack }: ForgotPasswordScreenProps) {
  const auth = useAuth();
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(schema), mode: "onSubmit" });

  const onSubmit = handleSubmit(async (values) => {
    // Never surface a failure that would reveal account existence; the server always 202s.
    await auth.forgotPassword(values.email).catch(() => undefined);
    setSent(true);
  });

  if (sent) {
    return (
      <AuthCard
        title="Check your email"
        subtitle="If an account exists for that address, a reset link is on its way. The link expires and can be used once."
        footer={
          <Button variant="link" type="button" onClick={onBack}>
            Back to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset password"
      subtitle="We'll email you a single-use link to set a new password."
      footer={
        <Button variant="link" type="button" onClick={onBack}>
          Back to sign in
        </Button>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          inputMode="email"
          placeholder="you@band.com"
          error={errors.email?.message}
          {...register("email")}
        />
        <Button variant="accent" type="submit" fullWidth loading={isSubmitting}>
          Send reset link
        </Button>
      </form>
    </AuthCard>
  );
}
